import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Archive,
    Eye,
    Layers3,
    Palette,
    PenSquare,
    PlusCircle,
    Power,
    Save,
    Search,
    ShieldCheck,
    Trash2,
    X,
} from "lucide-react";

import {
    createCertificatRequest,
    createCertificatVersionRequest,
    deleteCertificatRequest,
    fetchCertificatPlaceholdersRequest,
    fetchCertificatsRequest,
    updateCertificatActivationRequest,
    updateCertificatArchiveRequest,
    updateCertificatPublicationRequest,
    updateCertificatRequest,
} from "../../../../../redux/actions/certificatsActions";
import {
    buildPreviewHtml,
    createEmptyCertificatForm,
    normalizeCertificatForForm,
} from "./certificateBuilder";
import "../../RessourcesStyles/CertificatManagement.css";

const lifecycleTone = (certificat) => {
    if (certificat.archived) return "archived";
    if (certificat.publicationStatus === "PUBLISHED") return "published";
    return "draft";
};

const actionLabels = (certificat) => ({
    edit: "Modifier les informations générales",
    designer: "Configurer le template",
    version: "Créer une nouvelle version",
    activation: certificat.isActive ? "Désactiver" : "Activer",
    publication: certificat.publicationStatus === "PUBLISHED" ? "Remettre en brouillon" : "Publier",
    archive: certificat.archived ? "Restaurer" : "Archiver",
    delete: "Supprimer",
});

const nextPublicationStatus = (certificat) => (
    certificat.publicationStatus === "PUBLISHED" ? "DRAFT" : "PUBLISHED"
);

const actionButtonProps = (label) => ({
    title: label,
    "aria-label": label,
});

const statusLabel = (certificat) => {
    if (certificat.archived) return "Archivé";
    return certificat.publicationStatus === "PUBLISHED" ? "Publié" : "Brouillon";
};

const setNestedValue = (source, path, value) => {
    const keys = path.split(".");
    const next = { ...source };
    let current = next;
    keys.forEach((key, index) => {
        if (index === keys.length - 1) {
            current[key] = value;
            return;
        }
        current[key] = { ...(current[key] || {}) };
        current = current[key];
    });
    return next;
};

const readNestedValue = (source, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], source);

export default function CertificatManagement() {
    const dispatch = useDispatch();
    const { certificats = [], placeholders = [], loading = false } = useSelector((state) => state.certificats || {});

    const [query, setQuery] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [designerOpen, setDesignerOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(createEmptyCertificatForm());
    const [focusedField, setFocusedField] = useState("description");
    const [backgroundFile, setBackgroundFile] = useState(null);
    const [backgroundPreview, setBackgroundPreview] = useState("");

    useEffect(() => {
        dispatch(fetchCertificatsRequest());
        dispatch(fetchCertificatPlaceholdersRequest());
    }, [dispatch]);

    useEffect(() => {
        if (!backgroundFile) {
            setBackgroundPreview("");
            return undefined;
        }
        const objectUrl = URL.createObjectURL(backgroundFile);
        setBackgroundPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [backgroundFile]);

    const filteredCertificats = useMemo(() => {
        const term = query.trim().toLowerCase();
        return certificats.filter((item) => {
            if (!term) return true;
            return [item.nom, item.reference, item.versionGroupCode, item.description]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(term));
        });
    }, [certificats, query]);

    const selectedCertificat = useMemo(
        () => certificats.find((item) => item.id === selectedId) || null,
        [certificats, selectedId]
    );

    const versions = useMemo(
        () => certificats
            .filter((item) => selectedCertificat?.versionGroupCode && item.versionGroupCode === selectedCertificat.versionGroupCode)
            .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0)),
        [certificats, selectedCertificat]
    );

    const placeholderMap = useMemo(() => {
        const map = {};
        placeholders.forEach((item) => {
            map[item.key] = item.example || "";
        });
        return map;
    }, [placeholders]);

    const previewHtml = useMemo(
        () => buildPreviewHtml(
            form,
            placeholderMap,
            backgroundPreview || (form.attachmentFile ? `/api/certificats/files/${form.attachmentFile}` : "")
        ),
        [form, placeholderMap, backgroundPreview]
    );

    const openCreateForm = () => {
        setSelectedId(null);
        setForm(createEmptyCertificatForm());
        setBackgroundFile(null);
        setFormOpen(true);
    };

    const openEditForm = (certificat) => {
        setSelectedId(certificat.id);
        setForm(normalizeCertificatForForm(certificat));
        setBackgroundFile(null);
        setFormOpen(true);
    };

    const openDesigner = (certificat) => {
        setSelectedId(certificat.id);
        setForm(normalizeCertificatForForm(certificat));
        setFocusedField("description");
        setBackgroundFile(null);
        setDesignerOpen(true);
    };

    const updateField = (field, value) => {
        if (field.startsWith("designConfig.")) {
            setForm((prev) => setNestedValue(prev, field, value));
            return;
        }
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const insertPlaceholder = (token) => {
        const current = focusedField.startsWith("designConfig.")
            ? readNestedValue(form, focusedField) || ""
            : form[focusedField] || "";
        updateField(focusedField, `${current}${current ? " " : ""}${token}`);
    };

    const buildPayload = () => ({
        ...form,
        reference: form.reference || form.versionGroupCode,
        versionGroupCode: form.versionGroupCode || form.reference,
    });

    const saveGeneralForm = () => {
        if (!form.nom.trim() || !(form.reference || form.versionGroupCode).trim()) {
            Swal.fire({ icon: "warning", text: "Le nom et la référence sont obligatoires." });
            return;
        }
        const payload = { data: buildPayload(), image: null };
        if (selectedId) {
            dispatch(updateCertificatRequest(selectedId, payload));
        } else {
            dispatch(createCertificatRequest(payload));
        }
        setFormOpen(false);
    };

    const saveDesigner = () => {
        if (!selectedId) return;
        dispatch(updateCertificatRequest(selectedId, { data: buildPayload(), image: backgroundFile }));
        setDesignerOpen(false);
    };

    const confirmDelete = (certificat) => {
        Swal.fire({
            title: "Supprimer ce certificat ?",
            text: "S'il est déjà utilisé dans un module, le backend bloquera la suppression.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#b91c1c",
        }).then((result) => result.isConfirmed && dispatch(deleteCertificatRequest(certificat.id)));
    };

    return (
        <div className="cert-admin-page">
            <header className="cert-admin-header">
                <div>
                    <h2>Certificats</h2>
                </div>
            </header>

            <section className="cert-admin-card">
                <div className="cert-admin-body">
                    <div className="cert-admin-listbar">
                        <div className="cert-admin-search-wrap">
                            <Search size={14} className="cert-admin-search-icon" />
                            <input
                                className="cert-builder-search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Rechercher par nom, référence ou description..."
                            />
                        </div>
                        <button className="cert-builder-btn cert-builder-btn--primary" onClick={openCreateForm} type="button">
                            <PlusCircle size={16} /> Ajouter
                        </button>
                    </div>

                    <section className="cert-admin-table-wrap">
                        <table className="cert-admin-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Référence</th>
                                    <th>Version</th>
                                    <th>Statut</th>
                                    <th>Actif</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="cert-admin-loading">Chargement...</td>
                                    </tr>
                                ) : filteredCertificats.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="cert-admin-empty">Aucun certificat trouvé</td>
                                    </tr>
                                ) : (
                                    filteredCertificats.map((certificat) => (
                                        <tr key={certificat.id}>
                                            <td>
                                                <strong>{certificat.nom}</strong>
                                            </td>
                                            <td><span className="cert-admin-badge cert-admin-badge--blue">{certificat.reference}</span></td>
                                            <td><span className="cert-admin-badge cert-admin-badge--grey">v{certificat.versionNumber || 1}</span></td>
                                            <td><span className={`cert-card-item__status cert-card-item__status--${lifecycleTone(certificat)}`}>{statusLabel(certificat)}</span></td>
                                            <td><span className={`cert-admin-pill ${certificat.isActive ? "ok" : "off"}`}>{certificat.isActive ? "Oui" : "Non"}</span></td>
                                            <td className="is-right">
                                                <div className="cert-admin-actions">
                                                    <button {...actionButtonProps(actionLabels(certificat).edit)} onClick={() => openEditForm(certificat)} type="button"><PenSquare size={16} /></button>
                                                    <button {...actionButtonProps(actionLabels(certificat).designer)} onClick={() => openDesigner(certificat)} type="button"><Palette size={16} /></button>
                                                    <button {...actionButtonProps(actionLabels(certificat).version)} onClick={() => dispatch(createCertificatVersionRequest(certificat.id))} type="button"><Layers3 size={16} /></button>
                                                    <button {...actionButtonProps(actionLabels(certificat).activation)} onClick={() => dispatch(updateCertificatActivationRequest(certificat.id, !certificat.isActive))} type="button"><Power size={16} /></button>
                                                    <button {...actionButtonProps(actionLabels(certificat).publication)} onClick={() => dispatch(updateCertificatPublicationRequest(certificat.id, nextPublicationStatus(certificat)))} type="button"><ShieldCheck size={16} /></button>
                                                    <button {...actionButtonProps(actionLabels(certificat).archive)} onClick={() => dispatch(updateCertificatArchiveRequest(certificat.id, !certificat.archived))} type="button"><Archive size={16} /></button>
                                                    <button {...actionButtonProps(actionLabels(certificat).delete)} className="danger" onClick={() => confirmDelete(certificat)} type="button"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </section>
                </div>
            </section>

            {formOpen && (
                <div className="cert-modal-backdrop">
                    <section className="cert-modal cert-modal--narrow">
                        <header>
                            <h3>{selectedId ? "Modifier le certificat" : "Ajouter un certificat"}</h3>
                            <button onClick={() => setFormOpen(false)}><X size={18} /></button>
                        </header>
                        <div className="cert-form-grid">
                            <label><span>Nom</span><input value={form.nom} onChange={(event) => updateField("nom", event.target.value)} /></label>
                            <label><span>Référence</span><input value={form.reference} disabled={!!selectedId} onChange={(event) => updateField("reference", event.target.value.toUpperCase())} /></label>
                            <label className="cert-form-grid__full"><span>Description</span><textarea rows={5} value={form.description} onChange={(event) => updateField("description", event.target.value)} /></label>
                            <label><span>Signature</span><input value={form.signature} onChange={(event) => updateField("signature", event.target.value)} /></label>
                            <label><span>Statut</span><select value={form.publicationStatus} onChange={(event) => updateField("publicationStatus", event.target.value)}><option value="DRAFT">Brouillon</option><option value="PUBLISHED">Publié</option></select></label>
                        </div>
                        <footer><button className="cert-builder-btn cert-builder-btn--primary" onClick={saveGeneralForm}><Save size={16} /> Enregistrer</button></footer>
                    </section>
                </div>
            )}

            {designerOpen && (
                <div className="cert-modal-backdrop">
                    <section className="cert-modal cert-modal--designer">
                        <header>
                            <h3>Designer certificat</h3>
                            <button onClick={() => setDesignerOpen(false)}><X size={18} /></button>
                        </header>
                        <div className="cert-designer-layout">
                            <aside className="cert-designer-panel">
                                <div className="cert-form-grid">
                                    <label className="cert-form-grid__full"><span>Texte principal</span><textarea rows={5} value={form.description} onFocus={() => setFocusedField("description")} onChange={(event) => updateField("description", event.target.value)} /></label>
                                    <label><span>Image de fond</span><input type="file" accept="image/*" onChange={(event) => setBackgroundFile(event.target.files?.[0] || null)} /></label>
                                    <label><span>Disposition</span><select value={form.designConfig.layout.contentAlign} onChange={(event) => updateField("designConfig.layout.contentAlign", event.target.value)}><option value="center">Centré</option><option value="left">Gauche</option><option value="right">Droite</option></select></label>
                                    {[
                                        ["designConfig.palette.primary", "Couleur principale", "color"],
                                        ["designConfig.palette.secondary", "Couleur secondaire", "color"],
                                        ["designConfig.frame.color", "Couleur du cadre", "color"],
                                        ["designConfig.palette.title", "Couleur du titre", "color"],
                                        ["designConfig.palette.text", "Couleur du texte", "color"],
                                        ["designConfig.typography.titleSize", "Taille titre", "number"],
                                        ["designConfig.typography.recipientSize", "Taille nom", "number"],
                                        ["designConfig.typography.bodySize", "Taille texte", "number"],
                                    ].map(([path, label, type]) => (
                                        <label key={path}><span>{label}</span><input type={type} value={readNestedValue(form, path) || ""} onChange={(event) => updateField(path, type === "number" ? Number(event.target.value) : event.target.value)} /></label>
                                    ))}
                                    {["eyebrow", "intro", "footerNote", "signatureLabel"].map((block) => (
                                        <label key={block} className="cert-form-grid__full">
                                            <span>{block}</span>
                                            <textarea rows={2} value={form.designConfig.blocks[block] || ""} onFocus={() => setFocusedField(`designConfig.blocks.${block}`)} onChange={(event) => updateField(`designConfig.blocks.${block}`, event.target.value)} />
                                        </label>
                                    ))}
                                </div>
                                <div className="cert-placeholder-library">
                                    <div className="cert-placeholder-library__header"><Eye size={15} /> Champs dynamiques</div>
                                    <div className="cert-placeholder-library__chips">
                                        {placeholders.map((placeholder) => (
                                            <button key={placeholder.key} onClick={() => insertPlaceholder(placeholder.key)} title={`${placeholder.label} - ${placeholder.example}`}>
                                                {placeholder.group} · {placeholder.key}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {versions.length > 1 && (
                                    <div className="cert-version-panel">
                                        {versions.map((version) => (
                                            <button key={version.id} className={`cert-version-card ${version.id === selectedId ? "is-current" : ""}`} onClick={() => openDesigner(version)}>
                                                <strong>{version.reference}</strong><span>Version {version.versionNumber}</span><span>{statusLabel(version)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </aside>
                            <main className="cert-builder-preview cert-builder-preview--modal">
                                <div className="cert-preview-frame" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                            </main>
                        </div>
                        <footer><button className="cert-builder-btn cert-builder-btn--primary" onClick={saveDesigner}><Save size={16} /> Sauvegarder le template</button></footer>
                    </section>
                </div>
            )}
        </div>
    );
}
